import { useState } from "react";
import { forgotPassword, resetPassword } from "@/lib/api";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (e) { toast.error("Request failed. Please try again."); } finally { setLoading(false); }
  };

  return (
    <div className="pt-16 min-h-screen" data-testid="forgot-password-page">
      <div className="max-w-md mx-auto px-6 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2">Account Recovery</p>
        <h1 className="font-heading text-4xl font-bold mb-6">Forgot Password</h1>

        {!sent ? (
          <form onSubmit={submit} className="space-y-3">
            <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="Your account email" className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="forgot-email"/>
            <button disabled={loading} className="w-full btn-gold py-3 rounded-full font-semibold" data-testid="forgot-submit">{loading?"Working…":"Send reset link"}</button>
          </form>
        ) : (
          <div className="space-y-4 bg-[#0A0A0E] border border-[#D4AF37]/40 rounded-xl p-6" data-testid="reset-sent-confirm">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
              <p className="font-heading text-lg">Check your email</p>
            </div>
            <p className="text-sm text-white/70">If an account exists for that address, a password reset link has been sent. The link will expire in 1 hour.</p>
            <p className="text-xs text-white/40">Didn't receive an email? Check your spam folder, or contact support.</p>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-white/40"><Link to="/login" className="hover:text-white">← Back to sign in</Link></p>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await resetPassword(token, pw); toast.success("Password reset! Please sign in."); nav("/login");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); } finally { setLoading(false); }
  };

  if (!token) return <div className="pt-24 text-center text-white/60">Invalid reset link.</div>;

  return (
    <div className="pt-16 min-h-screen" data-testid="reset-password-page">
      <div className="max-w-md mx-auto px-6 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2">New Password</p>
        <h1 className="font-heading text-4xl font-bold mb-6">Reset Password</h1>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" required minLength={6} value={pw} onChange={e=>setPw(e.target.value)} placeholder="New password (min 6)" className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="new-password"/>
          <button disabled={loading} className="w-full btn-gold py-3 rounded-full font-semibold" data-testid="reset-submit">{loading?"Working…":"Set new password"}</button>
        </form>
      </div>
    </div>
  );
}
